import { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import {
  ChevronDown,
  CheckCircle2,
  AlertTriangle,
  MessageCircle,
} from "lucide-react";
import type { BiomarkerSummary } from "@/services/report-summary";
import { GradientMesh } from "@/components/gradient-mesh";

function ScrollIndicator() {
  return (
    <motion.div
      className="flex flex-col items-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.5, duration: 0.8 }}
    >
      <motion.div
        animate={{ y: [0, 6, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="rounded-full bg-slate-950/55 px-2 py-1.5 shadow-lg backdrop-blur-md ring-1 ring-white/30">
          <ChevronDown className="size-4 text-white drop-shadow" />
        </div>
      </motion.div>
    </motion.div>
  );
}

function BiomarkerSlide({
  marker,
  variant,
  index,
}: {
  marker: BiomarkerSummary;
  variant: "good" | "attention";
  index: number;
}) {
  const isGood = variant === "good";
  const backdropSrc =
    marker.name === "Concentration"
      ? "/videos/concentration.mp4"
      : marker.name === "Volume"
        ? "/videos/volume.mp4"
      : marker.name === "Progressive motility"
        ? "/videos/motility.mp4"
        : null;
  const sentences = marker.commentary
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <section className="relative flex h-dvh snap-start snap-always flex-col justify-center overflow-hidden bg-surface">
      {backdropSrc && (
        <video
          src={backdropSrc}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      <div className="pointer-events-none absolute inset-0 bg-white/52" />
      <GradientMesh />

      <div className="relative z-10 px-5 py-14 md:px-10 md:py-16">
        <div className="mx-auto grid w-full max-w-5xl gap-4 md:grid-cols-2 md:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.12 }}
            className="flex min-h-[150px] items-center justify-center rounded-3xl border border-slate-200/70 bg-white/80 p-5 text-center shadow-sm md:min-h-[280px] md:p-7"
          >
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {marker.name}
              </p>
              <p
                className={`mt-2 text-4xl font-semibold tracking-tight md:text-5xl ${
                  isGood ? "text-emerald-700" : "text-amber-700"
                }`}
              >
                {marker.value}
              </p>
              <p className="mt-2 text-sm text-slate-600">
                Reference: <span className="font-medium text-slate-700">{marker.reference}</span>
              </p>
            </div>
          </motion.div>

          <div className="min-h-[210px] rounded-3xl border border-slate-200/70 bg-white/80 p-5 shadow-sm md:min-h-[280px] md:p-7">
            <div className="h-full overflow-y-auto pr-1 scrollbar-thin">
              {sentences.length > 0 ? (
                sentences.map((line, i) => (
                  <motion.p
                    key={`${marker.name}-comment-${i}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.18 + i * 0.18 }}
                    className="text-center text-base leading-relaxed text-slate-700 md:text-lg"
                  >
                    {line}
                  </motion.p>
                ))
              ) : (
                <p className="text-center text-base leading-relaxed text-slate-700 md:text-lg">
                  This result is included in your summary.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-7 left-1/2 z-20 -translate-x-1/2 md:bottom-9">
        <ScrollIndicator />
      </div>
    </section>
  );
}

export default function ReportSummary({
  good,
  attention,
  videoUrl,
  onStartChat,
}: {
  good: BiomarkerSummary[];
  attention: BiomarkerSummary[];
  videoUrl: string | null;
  onStartChat: () => void;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);
  const goodRef = useRef<HTMLDivElement>(null);
  const attentionRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  const heroInView = useInView(heroRef, { once: true });
  const { scrollYProgress } = useScroll({ container: scrollerRef });
  const heroBackdropOpacity = useTransform(
    scrollYProgress,
    [0, 0.06, 0.16],
    [0.24, 0.42, 0.78],
  );
  const videoInView = useInView(videoRef, { once: true, margin: "-60px" });
  const goodInView = useInView(goodRef, { once: true, margin: "-60px" });
  const attentionInView = useInView(attentionRef, { once: true, margin: "-60px" });
  const ctaInView = useInView(ctaRef, { once: true, margin: "-60px" });

  const hasVideo = !!videoUrl;
  const hasAttention = attention.length > 0;

  return (
    <div
      ref={scrollerRef}
      className="h-dvh snap-y snap-mandatory overflow-y-auto scroll-smooth bg-surface"
    >
      {/* Section 1: Welcome */}
      <section
        ref={heroRef}
        className="relative flex h-dvh snap-start snap-always flex-col items-center justify-center px-6"
      >
        <motion.video
          src="/videos/backdrop.mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          onLoadedMetadata={(e) => {
            e.currentTarget.playbackRate = 0.7;
          }}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          style={{ opacity: heroBackdropOpacity }}
        />
        <div className="pointer-events-none absolute inset-0 bg-white/72" />
        <GradientMesh />
        <div className="relative z-10 max-w-md text-center">
          <motion.div
            className="mx-auto mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={heroInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <img src="/logo.png" alt="" className="mx-auto size-14" />
          </motion.div>

          <motion.h1
            className="text-3xl font-semibold leading-tight tracking-tight text-[#132238] sm:text-4xl"
            initial={{ opacity: 0, y: 24 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
          >
            Your results are ready
          </motion.h1>

          <motion.p
            className="mx-auto mt-4 max-w-xs text-base leading-relaxed text-slate-500"
            initial={{ opacity: 0, y: 14 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.7, ease: "easeOut" }}
          >
            Let's walk through your semen analysis together.
          </motion.p>
        </div>

        <div className="pointer-events-none absolute bottom-7 left-1/2 z-20 -translate-x-1/2 md:bottom-9">
          <ScrollIndicator />
        </div>
      </section>

      {/* Section 2: Motility video (if available) */}
      {hasVideo && (
        <section
          ref={videoRef}
          className="relative flex h-dvh snap-start snap-always flex-col items-center justify-center overflow-hidden"
        >
          <video
            src={videoUrl!}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="pointer-events-none absolute inset-0 bg-black/20" />
          <GradientMesh />
          <motion.div
            className="relative z-10 flex h-full w-full flex-col items-center justify-center px-6"
            initial={{ opacity: 0, y: 20 }}
            animate={videoInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="rounded-2xl border border-white/55 bg-white/35 px-5 py-3 shadow-md backdrop-blur-md">
              <p className="text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-700/90">
                CheckCells
              </p>
              <p className="mt-1 text-center text-base font-medium tracking-tight text-slate-900">
                This is your sample
              </p>
            </div>
          </motion.div>

          <div className="pointer-events-none absolute bottom-7 left-1/2 z-20 -translate-x-1/2 md:bottom-9">
            <ScrollIndicator />
          </div>
        </section>
      )}

      {/* Biomarker slides (one per marker) */}
      {good.map((m, i) => (
        <div key={`good-${m.name}`} ref={i === 0 ? goodRef : undefined}>
          <BiomarkerSlide marker={m} variant="good" index={i} />
        </div>
      ))}
      {attention.map((m, i) => (
        <div key={`attention-${m.name}`} ref={i === 0 ? attentionRef : undefined}>
          <BiomarkerSlide marker={m} variant="attention" index={i} />
        </div>
      ))}

      {/* Final section: CTA */}
      <section
        ref={ctaRef}
        className="relative flex h-dvh snap-start snap-always flex-col items-center justify-center px-6"
      >
        <GradientMesh />
        <motion.div
          className="relative z-10 max-w-md text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={ctaInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="mx-auto mb-6 flex size-14 items-center justify-center rounded-2xl bg-white shadow-sm">
            <img src="/brain-9-svgrepo-com.svg" alt="" className="size-7" />
          </div>

          <h2 className="text-3xl font-semibold tracking-tight text-[#132238]">
            Have questions?
          </h2>
          <p className="mt-3 text-base leading-relaxed text-slate-500">
            Your AI companion is here to help you understand every detail of
            your results.
          </p>

          <motion.button
            onClick={onStartChat}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#132238] px-8 py-3.5 text-sm font-semibold text-white shadow-lg transition hover:bg-[#1e3350]"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <MessageCircle className="size-4" />
            Ask your companion
          </motion.button>
        </motion.div>
      </section>
    </div>
  );
}
